<template>
  <form @submit.prevent="save" @keydown="form.onKeydown($event)">

    <!-- Name -->
    <div class="form-group row">
      <label for="name" class="col-3 col-form-label text-lg-right">{{ $t('name') }}</label>
      <div class="col-md-7">
        <input id="name" v-model="form.name" :class="{ 'is-invalid': form.errors.has('name') }" type="text" class="form-control">
        <form-error :form="form" field="name"/>
      </div>
    </div>

    <!-- Short Name -->
    <div class="form-group row">
      <label for="short_name" class="col-3 col-form-label text-lg-right">{{ $t('short_name') }}</label>
      <div class="col-md-7">
        <input id="short_name" v-model="form.short_name" :class="{ 'is-invalid': form.errors.has('short_name') }" type="text" class="form-control">
        <form-error :form="form" field="short_name"/>
      </div>
    </div>

    <!-- Reference number -->
    <div class="form-group row">
      <label for="reference_number" class="col-3 col-form-label text-md-right">{{ $t('organization_number') }}</label>
      <div class="col-sm-7">
        <input id="reference_number" v-model="form.reference_number" :class="{ 'is-invalid': form.errors.has('reference_number') }" type="text" name="reference_number" class="form-control">
        <form-error :form="form" field="reference_number"/>
      </div>
    </div>

    <!-- Description -->
    <div class="form-group row">
      <label for="description" class="col-3 col-form-label text-lg-right">{{ $t('description') }}</label>
      <div class="col-md-7">
        <textarea id="description"
                  v-model="form.description"
                  :placeholder="$t('organization_description')"
                  name="description"
                  class="form-control"
                  rows="5"/>
        <form-error :form="form" field="description"/>
      </div>
    </div>

    <div class="form-group row">
      <hr width="100%">
    </div>

    <!-- Address -->
    <div class="form-group row">
      <label for="address" class="col-md-3 col-form-label text-md-right">{{ $t('address') }}</label>
      <div class="col-md-7">
        <input id="address" v-model="form.address" :class="{ 'is-invalid': form.errors.has('address') }" class="form-control" type="text" name="address">
        <form-error :form="form" field="address"/>
      </div>
    </div>

    <!-- Location -->
    <div class="form-group row">
      <label for="postal_code" class="col-md-3 col-form-label text-md-right">{{ $t('postal_code') }} / {{ $t('location') }}</label>
      <div class="col-md-2">
        <input id="postal_code" v-model="form.postal_code" :class="{ 'is-invalid': form.errors.has('postal_code') }" :placeholder="$t('postal_code')" class="form-control" type="text" name="postal_code">
        <form-error :form="form" field="postal_code"/>
      </div>
      <div class="col-md-5">
        <input id="location" v-model="form.location" :class="{ 'is-invalid': form.errors.has('location') }" :placeholder="$t('location_placeholder')" class="form-control" type="text" name="location">
        <form-error :form="form" field="location"/>
      </div>
    </div>

    <!-- Country -->
    <div class="form-group row">
      <label for="country" class="col-md-3 col-form-label text-md-right">{{ $t('country') }}</label>
      <div class="col-md-7">
        <select id="country" v-model="form.country" class="custom-select" name="country">
          <option v-for="nationality in translateAndSort(nationalities)" :value="nationality.id">{{ nationality.name }}</option>
        </select>
        <form-error :form="form" field="country"/>
      </div>
    </div>

    <div class="form-group row">
      <hr width="100%">
    </div>

    <!-- Email -->
    <div class="form-group row">
      <label for="emailorg" class="col-md-3 col-form-label text-md-right">{{ $t('email') }}</label>
      <div class="col-md-7">
        <input id="emailorg" v-model="form.email" :class="{ 'is-invalid': form.errors.has('email') }" class="form-control" type="email" name="emailorg" placeholder="contact@myorganization.ch">
        <form-error :form="form" field="email"/>
      </div>
    </div>

    <!-- Phone -->
    <div class="form-group row">
      <label for="phone" class="col-md-3 col-form-label text-md-right">{{ $t('phone') }}</label>
      <div class="col-md-7">
        <input id="phone" v-model="form.phone" :class="{ 'is-invalid': form.errors.has('phone') }" class="form-control" type="tel" name="phone" placeholder="(+41)0123456789">
        <form-error :form="form" field="phone"/>
      </div>
    </div>

    <div class="form-group row">
      <hr width="100%">
    </div>

    <!-- Logo -->
    <div class="form-group row">
      <label class="col-md-3 col-form-label text-md-right">{{ $t('logo') }} (max: 300x300px)</label>
      <div class="col-md-7">
        <b-form-file id="imagefile" ref="logoForm" v-model="currentLogo" :placeholder="$t('choose_a_file')" @change="onLogoSelected"/>
        <span v-if="errorImage" class="invalid-feedback">{{ this.errorImage }}</span>
        <div v-if="currentLogo" class="mt-2">
          <img id="logo" ref="logo" class="rounded mx-auto d-block img-fluid img-thumbnail" src="#" >
        </div>
      </div>
      <div v-if="currentLogo">
        <a class="btn btn-danger" href="#" @click.prevent="deleteLogo(form.id)">
          {{ $t('delete') }}
        </a>
      </div>
    </div>

  </form>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import Form from 'vform'
import { translateAndSort } from '~/plugins/i18n'
import { mapActions, mapGetters } from 'vuex'
import { isManager, isAdmin } from '~/plugins/auth'

export default {
  name: 'OrganizationEditor',

  props: {
    organization: { type: Object, required: false }
  },

  mounted () {
    if (this.organization) {
      this.edit(this.organization)
    }
  },

  computed: {
    ...mapGetters({
      nationalities: 'nationalities/nationalities',
      organizations: 'organizations/organizations',
      user: 'auth/user'
    })
  },

  data: () => ({
    form: new Form({
      id: null,
      name: '',
      short_name: '',
      reference_number: '',
      description: '',
      address: '',
      postal_code: '',
      country: 1,
      location: '',
      email: '',
      phone: ''
    }),
    status: {},
    image: '',
    errorImage: '',
    currentLogo: ''
  }),

  methods: {
    reset () {
      this.form.reset()
      this.form.errors.clear()
      this.currentLogo = ''
      this.errorImage = ''
      this.image = null
      // $("#imagefile").val("");
    },

    edit (organization) {
      this.reset()

      if (!organization) {
        return
      }

      // Replace values in form.
      this.form.id = organization.id
      this.form.name = organization.name
      this.form.description = organization.description
      this.form.email = organization.email
      this.form.phone = organization.phone
      this.form.short_name = organization.short_name
      this.form.reference_number = organization.reference_number
      this.form.address = organization.address
      this.form.postal_code = organization.postal_code
      this.form.country = organization.country
      this.form.location = organization.location
      this.currentLogo = organization.logo

      if (this.currentLogo) {
        var image = new Image()
        image.src = organization.logo
        image.onload = function () {
          var w = image.width
          var h = image.height
          var ratio = h / w
          var targetWidth = w > 300 ? 300 : w
          $('#logo').attr('src', organization.logo).width(targetWidth).height(targetWidth * ratio)
        }
      }
    },

    create () {
      this.$refs.logoForm.reset()
      this.reset()
    },

    async save () {
      let ok = false

      this.$emit('saving')

      // Update or create.
      if (this.form.id > 0) {
        await this.form.patch('/api/organizations/' + this.form.id)
          .then((response) => { ok = true })
          .catch((error) => {})
      } else {
        await this.form.post('/api/organizations')
          .then((response) => {
            ok = true
            this.form.id = response.data.id
          })
          .catch((error) => {})
      }

      // If organization has been stored/updated...
      if (ok) {
        // Fetch the organizations again.
        await this.$store.dispatch('organizations/fetchOrganizations')
        const organization = this.organizations.find(o => o.id == this.form.id)

        // Update logo.
        if (this.image && organization) {
          console.log('uploading image')
          let formImg = new FormData()
          formImg.append('logo', this.image)

          const config = {
            headers: { 'content-type': 'multipart/form-data' }
          }

          // TODO : Use patch or put if somehow it can work someday
          await axios.post(`/api/organizations/${this.form.id}/logo`, formImg, config)
            .then((response) => {
              ok &= true
              organization.logo = response.data.logo
              this.organizations.find(o => o.id == organization.id).logo = response.data.logo
              this.$refs.logoForm.reset() // Reset form here to avoid bugs when calling 'edit' after 'save'.
            })
            .catch((error) => {
              ok &= false
              this.errorImage = error.response.data.errors.logo[0]
            })
        }

        // Update user's organization if necessary.
        if (this.user.organization && this.user.organization.id == organization.id) {
          this.user.organization = organization
        }
      }

      // Notify status.
      this.$emit(ok ? 'saved' : 'failed')
    },

    deleteLogo (id) {
      // Ask the server to delete the logo if the organization exists.
      if (id) {
        axios.delete(`/api/organizations/${id}/logo`)
          .then((response) => this.image = '')
          .catch((error) => this.errorImage = error.response.data.errors)
      }

      this.$refs.logoForm.reset()
      this.currentLogo = ''
      this.errorImage = ''
      this.image = null
      // $("#imagefile").val("");
    },

    onLogoSelected (ev) {
      this.image = ev.target.files[0]
      this.readAndDisplayImage(this.image)
    },

    readAndDisplayImage (image) {
      $('#logo').attr('src', '#')
      var reader = new FileReader()
      reader.onload = function (e) {
        var image = new Image()
        image.src = e.target.result
        image.onload = function () {
          var w = image.width
          var h = image.height
          var ratio = h / w
          var targetWidth = w > 300 ? 300 : w
          $('#logo').attr('src', e.target.result).width(targetWidth).height(targetWidth * ratio)
        }
      }
      reader.readAsDataURL(image)
    },

    isAdmin () {
      return isAdmin(this.user)
    },

    isManager (user) {
      return isManager(user)
    },

    ...mapActions([
      'organizations/deleteOrganization'
    ]),

    translateAndSort (nationalities) {
      return translateAndSort(nationalities, 'nationalities')
    }
  }
}
</script>



// WEBPACK FOOTER //
// ../../../../resources/js/components/OrganizationEditor.vue